import { Link } from "react-router-dom";
import { ArrowUpRight, Check, Clock, Rocket, Sparkles, Package, Send } from "lucide-react";

const stages = [
  { key: "brief", label: "Brief Received", Icon: Check },
  { key: "production", label: "In Production", Icon: Rocket },
  { key: "ai", label: "AI Rendering", Icon: Sparkles },
  { key: "polish", label: "Final Polish", Icon: Package },
  { key: "delivered", label: "Delivered", Icon: Send },
];

// Map project.sprint_progress (0-100) to a stage index
const progressToStageIndex = (progress) => {
  if (progress >= 100) return 4;
  if (progress >= 80) return 3;
  if (progress >= 50) return 2;
  if (progress >= 25) return 1;
  return 0;
};

export default function PortalProjectTracker({ projects, loading }) {
  return (
    <section
      className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10 border-t border-[#1F2833]"
      data-testid="portal-project-tracker"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#B91C2C] mb-2">
            // ACTIVE.PROJECTS
          </div>
          <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight">
            Live project pipeline.
          </h2>
        </div>
        <div className="font-mono text-[11px] text-[#C5C6C7]/70">
          {projects.length} tracked
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-sm text-[#C5C6C7]">
          &gt; loading projects<span className="te-caret">_</span>
        </div>
      ) : projects.length === 0 ? (
        <div
          className="border border-[#1F2833] p-10 text-center"
          data-testid="portal-empty-projects"
        >
          <Clock size={24} className="text-[#B91C2C] mx-auto" />
          <h3 className="mt-4 font-display text-xl">No active projects.</h3>
          <p className="mt-2 text-sm text-[#C5C6C7]">
            Kick one off from the service catalog above — go from brief to delivery in one flow.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {projects.map((p) => (
            <ProjectRow key={p.id} project={p} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProjectRow({ project }) {
  const activeIdx = progressToStageIndex(project.sprint_progress || 0);

  return (
    <div
      data-testid={`tracker-project-${project.id}`}
      className="bg-[#0d1117] border border-[#1F2833] p-5 lg:p-6 hover:border-[#FF3355]/50 transition-all"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className={`inline-block h-2 w-2 rounded-full ${
                project.status === "active"
                  ? "bg-emerald-400 te-pulse-dot"
                  : project.status === "shipped"
                  ? "bg-[#FF3355]"
                  : "bg-[#C5C6C7]/50"
              }`}
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7]">
              {project.status}
            </span>
          </div>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#F5F5F7]">
            {project.name}
          </h3>
          {project.description && (
            <p className="mt-1 text-xs text-[#C5C6C7] line-clamp-1 max-w-2xl">
              {project.description}
            </p>
          )}
        </div>
        <Link
          to={`/portal/projects/${project.id}`}
          data-testid={`tracker-open-${project.id}`}
          className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.22em] text-[#FF3355] hover:text-[#F5F5F7] transition-colors shrink-0"
        >
          Open <ArrowUpRight size={12} />
        </Link>
      </div>

      {/* Stage tracker */}
      <div className="mt-6 grid grid-cols-5 gap-2">
        {stages.map((s, i) => {
          const done = i < activeIdx;
          const active = i === activeIdx;
          const { Icon } = s;
          return (
            <div key={s.key} className="flex flex-col items-center text-center">
              <div
                className={`h-8 w-8 flex items-center justify-center border transition-all ${
                  done
                    ? "border-emerald-400 bg-emerald-400/10 text-emerald-400"
                    : active
                    ? "border-[#FF3355] bg-[#FF3355]/10 text-[#FF3355]"
                    : "border-[#1F2833] text-[#C5C6C7]/40"
                }`}
              >
                <Icon size={13} />
              </div>
              <div
                className={`mt-2 font-mono text-[9px] uppercase tracking-[0.15em] leading-tight ${
                  done
                    ? "text-emerald-400/80"
                    : active
                    ? "text-[#FF3355]"
                    : "text-[#C5C6C7]/50"
                }`}
              >
                {s.label}
              </div>
            </div>
          );
        })}
      </div>

      {/* Progress bar */}
      <div className="mt-5">
        <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] text-[#C5C6C7] mb-1.5">
          <span>sprint progress</span>
          <span className="text-[#FF3355]">{project.sprint_progress || 0}%</span>
        </div>
        <div className="h-1.5 bg-[#1F2833] relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#B91C2C] via-[#FF3355] to-emerald-400 transition-all duration-500"
            style={{ width: `${project.sprint_progress || 0}%` }}
          />
        </div>
      </div>
    </div>
  );
}
